import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { Icon, type IconName } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { SelectableCard } from '../ui/SelectableCard.js';
import type { ChannelContactDecisionEmail } from '@evo-hub/shared';

type Decision = ChannelContactDecisionEmail;

interface Props {
  messageId: number;
  fromEmail: string;
  onClose: () => void;
  onDecided: (decision: Decision, contactId?: number) => void;
}

export function ContactDecisionModal({ messageId, fromEmail, onClose, onDecided }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Decision | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post<{ ok: boolean; action: string; contactId?: number }>(
        `/api/contacts/from-email/${messageId}`,
        { decision: selected },
      );
      onDecided(selected, res.contactId);
    } catch {
      setError(t('contact.decision.toast_error'));
    } finally {
      setLoading(false);
    }
  };

  const options: Array<{ value: Decision; labelKey: string; desc: string; icon: IconName }> = [
    {
      value:    'saved_with_markdown',
      labelKey: 'contact.decision.save_with_markdown',
      desc:     t('contact.decision.save_with_markdown_desc'),
      icon:     'activity',
    },
    {
      value:    'saved_only',
      labelKey: 'contact.decision.save_only',
      desc:     t('contact.decision.save_only_desc'),
      icon:     'person',
    },
    {
      value:    'dismissed',
      labelKey: 'contact.decision.dismiss',
      desc:     t('contact.decision.dismiss_desc'),
      icon:     'close',
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={t('contact.decision.question')}
      description={t('contact.decision.title', { email: fromEmail })}
      size="sm"
      preventClose={loading}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="solid"
            tone="cyan"
            disabled={!selected || loading}
            loading={loading}
            onClick={() => void handleConfirm()}
          >
            {t('contact.decision.confirm')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map(opt => (
          <SelectableCard
            key={opt.value}
            selected={selected === opt.value}
            onSelect={() => setSelected(opt.value)}
            icon={<span style={{ color: 'var(--evo-fg-3)', display: 'flex' }}><Icon name={opt.icon} size={18} /></span>}
            title={t(opt.labelKey)}
            description={opt.desc}
          />
        ))}
        {error && (
          <p className="text-xs mt-1" style={{ color: 'var(--evo-danger)' }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}
